import isPropValid from "@emotion/is-prop-valid";
import React, { useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer/dist/index.legacy.esm.js";
import { BrowserRouter } from "react-router-dom";
import { StyleSheetManager } from "styled-components";

import { Config } from "../shared/core/config/config";
import { authenticationManager, errorStore, webAppStartManager } from "../shared/core/service/services";
import { useObservable } from "../shared/utils/observable";
import { AppRouter } from "./core/routing/app-router";
import { BootSplash } from "./ui/common/boot-splash";
import { Modal, ModalContainer } from "./ui/common/modal/modal";
import { ErrorScreen } from "./ui/screen/error/error-screen";

// styled-components v6 dropped the automatic prop filtering v5 did: without this guard
// every prop that is not prefixed with "$" reaches the underlying HTML element.
const shouldForwardProp = (prop: string, target: unknown) => (typeof target === "string" ? isPropValid(prop) : true);

export function App() {
  const [initialized, setInitialized] = useState(false);
  const isConnected = useObservable(authenticationManager.isConnected);
  const isAuthenticated = useObservable(authenticationManager.isAuthenticated);
  const errorHandler = useObservable(errorStore.error);
  const [showErrorScreen, setShowErrorScreen] = useState(false);

  useEffect(() => {
    if (!errorHandler) {
      async function initializeWebApp() {
        try {
          if (webAppStartManager && webAppStartManager.startApp) {
            await webAppStartManager.startApp();
            setInitialized(true);
          } else {
            throw new Error("webAppStartManager or webAppStartManager.startApp is undefined");
          }
        } catch (error) {
          setShowErrorScreen(true);
        }
      }

      initializeWebApp();
    }
  }, []);

  useEffect(() => {
    if (errorHandler && showErrorScreen) {
      errorStore.error.set(null);
    }
  }, [errorHandler, showErrorScreen]);

  useIdleTimer({
    // 5 minutes
    timeout: 1000 * 60 * 5,
    onIdle: () => handleOnIdle(),
    debounce: 5000,
  });

  const handleOnIdle = async () => {
    if (isConnected && isAuthenticated) {
      await authenticationManager.logout(true, true);
      Modal.dismissAll();
    }
  };

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      {showErrorScreen ? (
        <ErrorScreen />
      ) : initialized ? (
        <BrowserRouter basename={Config.BASE_URL}>
          <AppRouter />
          <ModalContainer />
        </BrowserRouter>
      ) : (
        <BootSplash />
      )}
    </StyleSheetManager>
  );
}
